import React from 'react';

type MembershipCredentialsSkeletonProps = {
  variant?: 'desktop' | 'mobile';
  count?: number;
};

const MembershipCardSkeleton: React.FC<{ variant: 'desktop' | 'mobile' }> = ({ variant }) => {
  const isDesktop = variant === 'desktop';

  return (
    <div className="border border-[#E9E9E9] rounded-xl overflow-hidden relative">
      <div className="absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent z-10 pointer-events-none" />

      <div
        className={`flex ${
          isDesktop ? 'flex-col md:flex-row md:items-center md:h-[170px]' : 'flex-col'
        } gap-5 md:gap-0 justify-between shadow-sm p-4 bg-white`}
      >
        <div
          className={`flex-shrink-0 ${
            isDesktop ? 'w-16 h-16 mr-6' : 'w-12 h-12'
          } bg-gray-200/70 rounded-lg border border-[#E9E9E9]/70 animate-pulse`}
        />

        <div className="flex flex-col flex-1 min-w-0 gap-3 md:gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="h-5 bg-gray-200/70 rounded w-40 animate-pulse" />
            <div className="h-5 bg-gray-200/70 rounded w-16 animate-pulse" />
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200/70 rounded w-full animate-pulse" />
            <div className="h-3 bg-gray-200/70 rounded w-5/6 animate-pulse" />
          </div>
          <div className="flex flex-row flex-wrap gap-2 mt-1">
            <div className="h-8 bg-gray-200/70 rounded-md w-36 animate-pulse" />
            <div className="h-8 bg-gray-200/70 rounded-md w-44 animate-pulse" />
          </div>
        </div>

        {isDesktop ? (
          <div className="flex flex-col items-end">
            <div className="h-10 bg-gray-200/70 rounded-md w-36 animate-pulse" />
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-2 w-full">
            <div className="h-10 bg-gray-200/70 rounded-md w-full animate-pulse" />
            <div className="h-10 bg-gray-200/70 rounded-md w-full animate-pulse" />
          </div>
        )}
      </div>
    </div>
  );
};

const MembershipCredentialsSkeleton: React.FC<MembershipCredentialsSkeletonProps> = ({
  variant = 'desktop',
  count = 3,
}) => {
  const isDesktop = variant === 'desktop';

  return (
    <div className="w-full" role="status" aria-label="Loading membership credentials">
      {isDesktop ? (
        <div className="rounded-xl h-auto bg-white relative">
          <div className="h-4 bg-gray-200/70 rounded w-56 mb-6 animate-pulse" />
        </div>
      ) : (
        <div className="flex justify-between items-center mb-4 min-w-0">
          <div className="h-4 bg-gray-200/70 rounded w-48 animate-pulse flex-1 mr-2" />
          <div className="w-10 h-10 bg-gray-200/70 rounded-lg animate-pulse flex-shrink-0" />
        </div>
      )}

      <div className="flex flex-col w-full gap-4">
        {Array.from({ length: count }).map((_, index) => (
          <MembershipCardSkeleton key={index} variant={variant} />
        ))}
      </div>
      <span className="sr-only">Loading membership credentials...</span>
    </div>
  );
};

export default MembershipCredentialsSkeleton;
